import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	IconButton,
	Input,
	JunkIcon,
	PencilEditBoxIcon,
	Popover,
	Radio,
	tokens,
} from '@treams/treams-ui';

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: 0;
	}
`;

const InputWrapper = styled.div`
	max-width: 400px;
	width: 100%;

	> div {
		margin-bottom: 0;
	}
`;

const SelectableArea = styled(FlexGap)`
	cursor: pointer;
`;

function normalizeTranslations(
	translations: graphql.LocalizedString | undefined,
	activeLanguageID: string,
	treamsLanguages: string[],
): graphql.LocalizedString {
	if (!translations || translations.length === 0) {
		return [
			{
				languageID: activeLanguageID,
				text: '',
			},
		];
	}

	const normalizedMap = new Map<string, string>();

	for (const item of translations) {
		const mappedLanguageID =
			treamsLanguages.find(
				(locale) => locale === item.languageID || locale.startsWith(`${item.languageID}_`),
			) ?? item.languageID;

		if (!normalizedMap.has(mappedLanguageID) || item.languageID === mappedLanguageID) {
			normalizedMap.set(mappedLanguageID, item.text);
		}
	}

	return Array.from(normalizedMap.entries()).map(([languageID, text]) => ({
		languageID,
		text,
	}));
}

export default function ExpertiseEdit(props: {
	expertise?: graphql.ExpertisePartialFragment;
	selected?: boolean;
	onSelect?: (expertiseID: string) => void;
	onDelete?: (expertiseID: string) => void;
	onCancel?: () => void;
	onRefresh: () => Promise<void> | void;
}) {
	const { t } = useTranslation();
	const { language, meta } = useLocalize();

	const initialTranslations = normalizeTranslations(
		props.expertise?.title,
		language.id,
		meta.treamsLanguages,
	);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			translations: initialTranslations,
		},
		onSubmit: () => {},
	});

	const [editing, setEditing] = useState(!props.expertise);
	const [confirmDelete, setConfirmDelete] = useState(false);

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading: loadingUpdate }] = graphql.useUpdateExpertiseMutation();
	const [create, { loading: loadingCreate }] = graphql.useCreateExpertiseMutation();
	const [archive, { loading: loadingArchive }] = graphql.useArchiveExpertiseMutation();

	const loading = loadingUpdate || loadingCreate || loadingArchive;

	const valid = formik.values.translations.some(
		(translation) => translation.text.trim().length > 0,
	);

	const inUse = Boolean(props.selected || (props.expertise?.careers.length ?? 0) > 0);

	const onEdit = () => {
		formik.resetForm({
			values: {
				translations: normalizeTranslations(
					props.expertise?.title,
					language.id,
					meta.treamsLanguages,
				),
			},
		});
		setEditing(true);
		reset();
	};

	const onCancel = () => {
		formik.resetForm();
		reset();
		setEditing(false);
		props.onCancel?.();
	};

	const onSave = attempt(async () => {
		const title = formik.values.translations
			.filter((translation) => translation.text.trim().length > 0)
			.map(({ languageID, text }) => ({ languageID, text: text.trim() }));

		formik.values.translations = title;

		if (props.expertise) {
			await update({
				variables: {
					input: {
						id: props.expertise.id,
						title,
					},
				},
			});
			setEditing(false);
		} else {
			const response = await create({
				variables: {
					input: {
						title,
					},
				},
			});

			if (response.data?.createExpertise.id) {
				props.onSelect?.(response.data.createExpertise.id);
			}
		}

		await props.onRefresh();
	});

	const onDelete = attempt(async () => {
		if (!props.expertise || inUse) {
			return;
		}

		await archive({
			variables: {
				input: {
					id: props.expertise.id,
				},
			},
		});

		props.onDelete?.(props.expertise.id);
		await props.onRefresh();
	});

	const title =
		formik.values.translations
			.filter(
				(translation) => translation.languageID === language.id && translation.text.length > 0,
			)
			.concat(formik.values.translations.filter((translation) => translation.text.length > 0))[0]
			?.text ?? '';

	if (editing) {
		return (
			<Row>
				<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0} flexGrow={1}>
					<GraphQLErrorMessage error={error} />
					<FormikProvider value={formik}>
						<InputWrapper>
							<Input.Localizable name="translations" />
						</InputWrapper>
					</FormikProvider>
				</FlexGap>
				<FlexGap alignItems="center" gap={tokens.gap.element} flexShrink={0}>
					<Button
						type="secondary"
						title={t('common.cancel')}
						onClick={onCancel}
						disabled={loading}
					/>
					<Button
						type="primary"
						title={t('common.save')}
						onClick={onSave}
						loading={loading}
						disabled={!valid}
					/>
				</FlexGap>
			</Row>
		);
	}

	const selectExpertise = () => {
		if (props.expertise) {
			props.onSelect?.(props.expertise.id);
		}
	};

	return (
		<Row>
			<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0} flexGrow={1}>
				<GraphQLErrorMessage error={error} />
				<SelectableArea alignItems="center" gap={tokens.gap.list} onClick={selectExpertise}>
					<Radio checked={props.selected} onChange={selectExpertise} />
					<Heading3 mb={0}>{title}</Heading3>
				</SelectableArea>
			</FlexGap>
			<FlexGap alignItems="center" gap={tokens.gap.element} flexShrink={0}>
				<IconButton
					type="secondary"
					icon={<PencilEditBoxIcon />}
					disabled={loading}
					onClick={onEdit}
				/>
				{props.expertise && (
					<Popover.Confirm
						visible={confirmDelete}
						title={t('common.confirm')}
						confirmTitle={t('common.delete')}
						cancelTitle={t('common.cancel')}
						confirmType="destructive"
						onCancel={() => setConfirmDelete(false)}
						onConfirm={async () => {
							setConfirmDelete(false);
							await onDelete();
						}}
					>
						<IconButton
							type="destructive"
							icon={<JunkIcon />}
							disabled={loading || inUse}
							onClick={() => setConfirmDelete(true)}
						/>
					</Popover.Confirm>
				)}
			</FlexGap>
		</Row>
	);
}
